import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";

// General Play: a member registers intent to play a round outside any
// organised competition (e.g. a club-announced open comp, or just playing
// whenever they like), then returns to the kiosk afterward to enter their
// score. This page is the admin side: pick the default course General
// Play rounds are played on, pick the Player List its members come from,
// turn the feature on/off, and review every round ever registered. Rounds
// are recorded for reference only — they do not feed back into anyone's
// society handicap automatically (confirmed with Mike 2026-10-19; a
// society handicap recorded here is also not an official WHS Handicap
// Index).
//
// The member source is configurable per society (confirmed with Mike
// 2026-10-19/20 — clubs manage membership differently: most on the
// Players page day-to-day, some via a dedicated Player List, many a mix
// of both). Leaving the list unset uses the live, active Players roster
// directly; picking a Player List (see pages/PlayerLists.jsx) uses that
// list's own name+handicap data instead, independent of whatever state
// the competition roster is in.
export default function GeneralPlay() {
  const [settings, setSettings] = useState(null);
  const [courses, setCourses] = useState([]);
  const [playerLists, setPlayerLists] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [detail, setDetail] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [deleteAllTyped, setDeleteAllTyped] = useState("");
  const [deletingAll, setDeletingAll] = useState(false);

  const load = () => {
    api.get("/general-play/settings").then(res => setSettings(res.data)).catch(() => setToast("Failed to load General Play settings"));
    api.get("/courses").then(res => setCourses(res.data)).catch(() => {});
    api.get("/player-lists").then(res => setPlayerLists(res.data)).catch(() => {});
    setLoadingHistory(true);
    api.get("/general-play/history").then(res => setHistory(res.data)).catch(() => {}).finally(() => setLoadingHistory(false));
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  // PUT replaces the whole settings row, so every save sends the full
  // current state with just one field overridden — never a partial patch
  // that would silently null out the other two fields.
  const patchSettings = async (patch) => {
    setSaving(true);
    try {
      const next = { enabled: settings.enabled, default_course_id: settings.default_course_id, player_list_id: settings.player_list_id, ...patch };
      await api.put("/general-play/settings", next);
      const res = await api.get("/general-play/settings");
      setSettings(res.data);
      showToast("General Play settings saved");
    } catch (e) {
      showToast("Failed: " + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/general-play/history/${id}`);
      setDetail(res.data);
    } catch { showToast("Failed to load round"); }
  };

  const deleteRound = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/general-play/history/${confirmDelete.id}`);
      setHistory(h => h.filter(r => r.id !== confirmDelete.id));
      setConfirmDelete(null);
      setDetail(null);
      showToast("Round deleted");
    } catch (e) {
      showToast("Failed to delete: " + (e.response?.data?.error || e.message));
    } finally {
      setDeleting(false);
    }
  };

  const deleteAllRounds = async () => {
    setDeletingAll(true);
    try {
      const res = await api.delete("/general-play/history");
      setHistory([]);
      setConfirmDeleteAll(false);
      setDeleteAllTyped("");
      setDetail(null);
      showToast(`Deleted ${res.data.deleted} round${res.data.deleted === 1 ? "" : "s"}`);
    } catch (e) {
      showToast("Failed to delete: " + (e.response?.data?.error || e.message));
    } finally {
      setDeletingAll(false);
    }
  };

  if (!settings) return <Layout><div className="p-6 text-gray-500">Loading...</div></Layout>;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <Layout>
      <div className="p-6 max-w-4xl">
        <h1 className="text-2xl font-bold text-green-700 mb-1">⛳ General Play</h1>
        <p className="text-sm text-gray-500 mb-6">Members register a round on the kiosk, play whenever suits them, then return to enter their score. Recorded for reference only — these rounds do not change anyone's society handicap automatically.</p>

        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Settings</h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.enabled} disabled={saving}
                onChange={e => patchSettings({ enabled: e.target.checked })}
                className="w-4 h-4 accent-green-600" />
              <span className="text-sm text-gray-700">Enabled on the kiosk</span>
            </label>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-sm text-gray-700 w-40">Default course</label>
            <select value={settings.default_course_id || ""} disabled={saving}
              onChange={e => patchSettings({ default_course_id: e.target.value ? parseInt(e.target.value, 10) : null })}
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 min-w-[260px]">
              <option value="">— Select a course —</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 w-40">Members</label>
            <select value={settings.player_list_id || ""} disabled={saving}
              onChange={e => patchSettings({ player_list_id: e.target.value ? parseInt(e.target.value, 10) : null })}
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 min-w-[260px]">
              <option value="">— Live Players roster (default) —</option>
              {playerLists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.entry_count})</option>)}
            </select>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {settings.player_list_id
              ? <>Members come from this Player List, not the main Players page. Manage it on the <a href="/player-lists" className="underline">Player Lists</a> page.</>
              : <>Members come directly from your <a href="/players" className="underline">Players</a> page (active players only). Prefer a separate list instead? Pick one above — manage it on the <a href="/player-lists" className="underline">Player Lists</a> page.</>}
          </p>
          {settings.enabled && !settings.default_course_id && (
            <p className="text-sm mt-3" style={{ color: "#b91c1c" }}>
              General Play is enabled but has no course set — the kiosk will refuse registrations until one is picked.
            </p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-800">General Play History</h2>
            {history.length > 0 && (
              <button
                onClick={() => setConfirmDeleteAll(true)}
                className="text-xs font-semibold text-red-600 hover:text-red-800 hover:underline"
              >Delete All</button>
            )}
          </div>
          {loadingHistory ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-500">No General Play rounds have been registered yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3">Registered</th>
                  <th className="py-2 pr-3">Member</th>
                  <th className="py-2 pr-3">Course</th>
                  <th className="py-2 pr-3">Handicap</th>
                  <th className="py-2 pr-3">Gross</th>
                  <th className="py-2 pr-3">Points</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {history.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pr-3 cursor-pointer" onClick={() => openDetail(r.id)}>{fmtDate(r.registered_at)}</td>
                    <td className="py-2 pr-3 cursor-pointer" onClick={() => openDetail(r.id)}>{r.player_name}</td>
                    <td className="py-2 pr-3 cursor-pointer" onClick={() => openDetail(r.id)}>{r.course_name || "—"}</td>
                    <td className="py-2 pr-3 cursor-pointer" onClick={() => openDetail(r.id)}>{r.handicap_snapshot ?? "—"}</td>
                    <td className="py-2 pr-3 cursor-pointer" onClick={() => openDetail(r.id)}>{r.gross_total ?? "—"}</td>
                    <td className="py-2 pr-3 font-semibold cursor-pointer" onClick={() => openDetail(r.id)}>{r.points_total ?? "—"}</td>
                    <td className="py-2 pr-3 cursor-pointer" onClick={() => openDetail(r.id)}>
                      {r.status === "completed"
                        ? <span style={{ color: "#166534" }} className="font-semibold">Completed</span>
                        : <span style={{ color: "#92400e" }} className="font-semibold">Awaiting Score</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete({ id: r.id, name: r.player_name }); }}
                        className="text-xs font-semibold text-red-600 hover:text-red-800 hover:underline"
                        title="Delete this round"
                      >Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {toast && <div className="fixed bottom-6 right-6 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm">{toast}</div>}

        {detail && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-lg" onClick={e => e.stopPropagation()}>
              <h2 className="text-xl font-bold text-gray-800 mb-1">{detail.player_name} — General Play</h2>
              <p className="text-sm text-gray-500 mb-4">{detail.course_name} · Registered {fmtDate(detail.registered_at)}{detail.submitted_at ? ` · Submitted ${fmtDate(detail.submitted_at)}` : ""} · Handicap at registration: {detail.handicap_snapshot ?? "—"}</p>
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-1 pr-3">Hole</th>
                    <th className="py-1 pr-3">Par</th>
                    <th className="py-1 pr-3">SI</th>
                    <th className="py-1 pr-3">Gross</th>
                    <th className="py-1 pr-3">Net</th>
                    <th className="py-1 pr-3">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.holes.map(h => (
                    <tr key={h.hole} className="border-b border-gray-100">
                      <td className="py-1 pr-3">{h.hole}</td>
                      <td className="py-1 pr-3">{h.par}</td>
                      <td className="py-1 pr-3">{h.stroke_index}</td>
                      <td className="py-1 pr-3">{h.gross ?? "—"}</td>
                      <td className="py-1 pr-3">{h.net ?? "—"}</td>
                      <td className="py-1 pr-3 font-semibold">{h.points ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-800">Gross total: {detail.gross_total ?? "—"} · Points: {detail.points_total ?? "—"}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setConfirmDelete({ id: detail.id, name: detail.player_name })}
                    className="font-semibold py-2 px-5 rounded-lg text-sm text-white bg-red-600 hover:bg-red-700"
                  >Delete</button>
                  <button onClick={() => setDetail(null)} className="font-semibold py-2 px-5 rounded-lg text-sm bg-gray-200 hover:bg-gray-300">Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {confirmDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !deleting && setConfirmDelete(null)}>
            <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-lg" onClick={e => e.stopPropagation()}>
              <h2 className="text-lg font-bold mb-2" style={{ color: "#b91c1c" }}>Delete this round?</h2>
              <p className="text-sm text-gray-600 mb-5">
                This permanently removes {confirmDelete.name}'s General Play round (and its hole scores) from history. This cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setConfirmDelete(null)} disabled={deleting} className="font-semibold py-2 px-5 rounded-lg text-sm bg-gray-200 hover:bg-gray-300">Cancel</button>
                <button onClick={deleteRound} disabled={deleting} className="font-semibold py-2 px-5 rounded-lg text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-60">
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmDeleteAll && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { if (!deletingAll) { setConfirmDeleteAll(false); setDeleteAllTyped(""); } }}>
            <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-lg" onClick={e => e.stopPropagation()}>
              <h2 className="text-lg font-bold mb-2" style={{ color: "#b91c1c" }}>Delete ALL General Play history?</h2>
              <p className="text-sm text-gray-600 mb-3">
                This permanently deletes all {history.length} round{history.length === 1 ? "" : "s"} (and every hole score) for this society. This cannot be undone.
              </p>
              <p className="text-sm text-gray-600 mb-2">Type <span className="font-mono font-semibold">DELETE</span> to confirm:</p>
              <input
                type="text"
                value={deleteAllTyped}
                onChange={e => setDeleteAllTyped(e.target.value)}
                disabled={deletingAll}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full mb-5 focus:outline-none focus:ring-2 focus:ring-red-500"
                autoFocus
              />
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => { setConfirmDeleteAll(false); setDeleteAllTyped(""); }} disabled={deletingAll} className="font-semibold py-2 px-5 rounded-lg text-sm bg-gray-200 hover:bg-gray-300">Cancel</button>
                <button
                  onClick={deleteAllRounds}
                  disabled={deletingAll || deleteAllTyped !== "DELETE"}
                  className="font-semibold py-2 px-5 rounded-lg text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-40"
                >
                  {deletingAll ? "Deleting..." : "Delete All"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
